// CRM-NANCOZI — Layout principal (Sidebar + Header)
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import {
  LayoutDashboard, Users, Briefcase,
  GraduationCap, Bell, BarChart3, Settings,
  UserCog, ClipboardList, LogOut, UserCircle,
} from 'lucide-react';
import logo from '../../img/logo1.png';

const NAV_ITEMS = [
  { label: 'Tableau de bord', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Clients',         icon: Users,           path: '/clients' },
  { label: 'Coachings',       icon: Briefcase,       path: '/coachings' },
  { label: 'Formations',      icon: GraduationCap,   path: '/formations' },
  { label: 'Notifications',   icon: Bell,            path: '/notifications', badge: 'j15' },
];

const ADMIN_ITEMS = [
  { label: 'Rapports',       icon: BarChart3,    path: '/rapports' },
  { label: 'Utilisateurs',   icon: UserCog,      path: '/utilisateurs' },
  { label: 'Journal audit',  icon: ClipboardList, path: '/audit' },
];

function Sidebar() {
  const { user } = useSelector(s => s.auth);
  const { kpis } = useSelector(s => s.dashboard);

  return (
    <aside className="sidebar">
      {/* Logo entreprise */}
      <div className="sidebar-logo">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src={logo}
            alt="NANCOZI"
            style={{
              width: 40, height: 40, objectFit: 'contain',
              borderRadius: 8, background: 'rgba(255,255,255,0.95)', padding: 4,
              flexShrink: 0,
            }}
          />
          <div>
            <h1>CRM-NANCOZI</h1>
            <p>Gestion de la Relation Client</p>
          </div>
        </div>
      </div>

      <nav className="sidebar-nav">
        <div className="nav-section-label">Menu principal</div>
        {NAV_ITEMS.map(({ label, icon: Icon, path, badge }) => (
          <NavLink key={path} to={path} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <Icon className="nav-icon" size={18} />
            {label}
            {badge === 'j15' && kpis.echeances_j15 > 0 && (
              <span className="nav-badge">{kpis.echeances_j15}</span>
            )}
          </NavLink>
        ))}

        {user?.role === 'administrateur' && (
          <>
            <div className="nav-section-label" style={{ marginTop: 16 }}>Administration</div>
            {ADMIN_ITEMS.map(({ label, icon: Icon, path }) => (
              <NavLink key={path} to={path} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <Icon className="nav-icon" size={18} />
                {label}
              </NavLink>
            ))}
          </>
        )}
      </nav>
    </aside>
  );
}

function Header({ title }) {
  const { user } = useSelector(s => s.auth);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const initiales = user ? `${user.prenom_util?.[0] || ''}${user.nom_util?.[0] || ''}`.toUpperCase() : 'U';

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <header className="header">
      <div className="header-title">{title}</div>

      {/* Infos utilisateur + déconnexion */}

      <div className="header-actions">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <NavLink to="/profil" title="Mon profil"
            style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none',
              padding: '4px 10px', borderRadius: 8, transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%',
              background: '#1F4E79', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 13, flexShrink: 0,
            }}>
              {initiales}
            </div>
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
                {user?.prenom_util} {user?.nom_util}
              </div>
              <div style={{ fontSize: 11, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                <UserCircle size={11} />
                {user?.role === 'administrateur' ? 'Administrateur' : 'Commercial'}
              </div>
            </div>
          </NavLink>
          <button
            onClick={handleLogout}
            title="Se déconnecter"
            style={{
              marginLeft: 8, padding: '7px 14px',
              background: '#C00000', border: 'none', borderRadius: 8,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              color: 'white', fontSize: 12, fontWeight: 600,
            }}
          >
            <LogOut size={14} />
            Déconnecter
          </button>
        </div>
      </div>
    </header>
  );
}

export default function MainLayout() {
  const { pathname: location } = useLocation();
  const titles = {
    '/dashboard':     'Tableau de bord',
    '/clients':       'Gestion des clients',
    '/coachings':     'Gestion des coachings',
    '/echeances':     'Suivi des échéances',
    '/formations':    'Gestion des formations',
    '/alertes':       'Gestion des alertes',
    '/notifications': 'Gestion des notifications',
    '/rapports':      'Rapports & Exports',
    '/profil':        'Mon profil',
    '/utilisateurs':  'Gestion des utilisateurs',
    '/audit':         "Journal d'audit",
    '/parametres':    'Paramètres',
  };
  const title = Object.entries(titles).find(([path]) => location.startsWith(path))?.[1] || 'CRM-NANCOZI';

  return (
    <div className="app-layout">
      <Sidebar />
      <Header title={title} />
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
